// src/domains/auth/auth.api.ts

import { apiAuthClient, apiRequest } from '@/services/api/api';
import axios from 'axios';
import {
  ChangePasswordRequestBody,
  LoginRequestBody,
  OtpResponse,
  PasswordResetRequestBody,
  TokenRequestBody,
  TokenResponse,
} from './auth.types';
import { API_ENDPOINTS, getFullEndpoint } from '@/services/api/endpoints';
import { genericResponse } from '@/shared/types/api';
import { showErrorToast } from '@/shared/notifications/toast';
import i18n from '@/shared/i18n/i18n';
import { createAppError, getAppErrorMessage } from '@/shared/errors';
import { handleAppError } from '@/shared/notifications/ErrorManager';

export async function login(payload: LoginRequestBody): Promise<OtpResponse> {
  const endpoint = getFullEndpoint('auth', API_ENDPOINTS.AUTH_LOGIN);

  try {
    const response = await apiAuthClient.post<
      OtpResponse,
      any,
      LoginRequestBody
    >(endpoint, payload);
    return response.data;
  } catch (error) {
    const appError = createAppError(error);
    if (appError.kind === 'auth') {
      showErrorToast(i18n.t('login.errors.invalidCredentials'));
      return Promise.reject(appError);
    }
    handleAppError(appError);
    return Promise.reject(appError);
  }
}

export async function getToken(
  payload: TokenRequestBody,
): Promise<TokenResponse> {
  const endpoint = getFullEndpoint('auth', API_ENDPOINTS.AUTH_TOKEN);
  try {
    const response = await apiAuthClient.post<
      TokenResponse,
      any,
      TokenRequestBody
    >(endpoint, payload);
    return response.data;
  } catch (error) {
    const appError = createAppError(error);
    if (appError.kind === 'auth') {
      showErrorToast(i18n.t('login.errors.invalidOtp'));
    } else {
      showErrorToast(
        i18n.t('general.errors.default', {
          statusText: getAppErrorMessage(appError),
        }),
      );
    }
    return Promise.reject(appError);
  }
}

export async function refreshToken(refreshTokenValue: string): Promise<{
  access_token: string;
  refresh_token?: string;
}> {
  const endpoint = getFullEndpoint('auth', API_ENDPOINTS.AUTH_REFRESH_TOKEN);
  try {
    const response = await apiAuthClient.post<
      { access_token: string; refresh_token?: string },
      any,
      { refresh_token: string }
    >(endpoint, { refresh_token: refreshTokenValue });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.error('Refresh token invalid or expired:', error);
      // no toast
      return Promise.reject(createAppError(error));
    }
    console.error('An unexpected error occurred during refresh token:', error);
    return Promise.reject(createAppError(error));
  }
}

export async function resetPassword(
  payload: PasswordResetRequestBody,
): Promise<genericResponse> {
  const endpoint = getFullEndpoint('auth', API_ENDPOINTS.AUTH_RESET_PASSWORD);
  return (
    await apiRequest<genericResponse, PasswordResetRequestBody>(
      'post',
      endpoint,
      payload,
    )
  ).data;
}

export async function otpForgotPassword(): Promise<genericResponse> {
  const endpoint = getFullEndpoint('auth', API_ENDPOINTS.AUTH_OTP);
  return (
    await apiRequest<genericResponse, void>('post', endpoint, undefined, true)
  ).data;
}

export async function changePassword(
  payload: ChangePasswordRequestBody,
): Promise<genericResponse> {
  const endpoint = getFullEndpoint('auth', API_ENDPOINTS.AUTH_CHANGE_PASSWORD);
  return (
    await apiRequest<genericResponse, ChangePasswordRequestBody>(
      'post',
      endpoint,
      payload,
      true,
    )
  ).data;
}

export async function logout(): Promise<genericResponse> {
  const endpoint = getFullEndpoint('auth', API_ENDPOINTS.AUTH_LOGOUT);
  return (
    await apiRequest<{ message: string }, void>(
      'post',
      endpoint,
      undefined,
      true,
    )
  ).data;
}
